// ThreadPanel — the right-side surface for threaded replies.
//
// The brief frames this as a quiet side-room: the timeline keeps its
// cadence while one question is given a place of its own. The panel
// slides in from the right with `--chat-motion-base`, closes on
// Escape, and persists its width to `localStorage`.
//
// State is composed: `useThread` owns the live reply list, the caller
// owns "which root", the component owns its own width + composer
// buffer. The thread-mode composer in this iteration is intentionally
// simple — a single-line-ish textarea with enter-to-send. The full
// composer toolbar (bold, emoji, attach, etc.) is the territory of the
// main-composer ticket and will be swapped in once `MessageComposer`
// lands.

import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type Ref,
  type RefObject,
} from "react";
import { Reply, Send, X } from "lucide-react";

import { AgentMentionPrompt } from "~/components/AgentMentionPrompt";
import type { ComposerSubmitMeta } from "~/components/MessageComposer";
import { DeleteConfirmDialog } from "~/components/DeleteConfirmDialog";
import { EmojiPicker } from "~/components/EmojiPicker";
import { DropZone, FileUploadButton, UploadProgressRow } from "~/components/FileUpload";
import { useImageLightbox } from "~/components/ImageLightbox";
import { InlineEdit } from "~/components/InlineEdit";
import { PersonAvatar } from "~/components/PersonAvatar";
import { ReactionBar } from "~/components/ReactionBar";
import { ReadReceipts } from "~/components/ReadReceipts";
import { isMediaEvent, MessageMediaBody } from "~/components/Timeline";
import { useToast } from "~/context/ToastContext";
import { useFileUpload } from "~/hooks/useFileUpload";
import { buildReceiptAvatarMap } from "~/lib/room-utils";
import { MarkdownBody } from "~/lib/markdown-render";
import {
  MessageActions,
  buildDeleteAction,
  buildEditAction,
  buildQuoteAction,
  buildReactAction,
  type MessageAction,
} from "~/components/MessageActions";
import { useThread } from "~/hooks/useThread";
import { useThreadReadingPosition } from "~/hooks/useThreadReadingPosition";
import { useDraft } from "~/hooks/useDraft";
import { useEditRecall } from "~/hooks/useEditRecall";
import { useMessageActions } from "~/hooks/useMessageActions";
import { useOwnAgentMentionGate } from "~/hooks/useOwnAgentMentionGate";
import { useAutoResizeTextarea } from "@nurion/ui/use-auto-resize-textarea";
import { useMatrix } from "~/context/MatrixContext";
import {
  COMPOSER_TEXTAREA_MAX_HEIGHT_VAR,
  COMPOSER_TEXTAREA_MIN_HEIGHT_VAR,
} from "~/lib/composer-sizing";
import {
  canMentionRoom,
  getMembers,
  getRoomEvent,
  getUserId,
  sendThreadReadReceipt,
  subscribeTimeline,
} from "~/lib/matrix";
import type { Client, Receipt, TimelineEvent, UserDirectoryEntry } from "~/lib/matrix";
import { insertQuote, type QuoteComposerHandle, type QuotePayload } from "~/lib/quote";
import { ownAgentUserId } from "~/lib/own-agent";
import { persistWidth, readPersistedWidth, readWidthBounds } from "~/lib/panel-width";
import { ICON_STROKE } from "~/lib/icon";
import { AutocompleteMenu, type AutocompleteMenuRef } from "~/components/AutocompleteMenu";
import {
  useAutocomplete,
  serialiseFormattedBody,
  realignMentions,
  shiftMentions,
  type MentionEdit,
  type MentionToken,
} from "~/hooks/useAutocomplete";
import {
  resolveUserSuggestions,
  type Suggestion,
  type OwnAgentSnapshot,
} from "~/lib/autocomplete-providers";
import {
  useOwnAgent,
  buildOwnAgentSnapshot,
  OWN_AGENT_CHANGED_COPY,
  OWN_AGENT_UNAVAILABLE_COPY,
  type OwnAgentScope,
} from "~/hooks/useOwnAgent";

function inputEventType(event: Event): string {
  return "inputType" in event && typeof event.inputType === "string" ? event.inputType : "";
}

const AUTO_SCROLL_THRESHOLD_PX = 50;
const READ_RECEIPT_DEBOUNCE_MS = 400;
const AUTOCOMPLETE_FETCH_DEBOUNCE_MS = 200;

/**
 * Extract a plain-text body for rendering. `TimelineEvent.content` is
 * a tagged union; the thread UI renders the `body` when present
 * (m.text / m.emote / m.notice / m.image / m.file) and falls back to
 * an empty string for state-event rows the thread panel does not yet
 * format specially.
 */
function readBody(event: TimelineEvent): string {
  const c = event.content;
  if (c.type === "m.redacted") return "Message removed";
  if ("body" in c && typeof c.body === "string") return c.body;
  return "";
}

const WIDTH_STORAGE_KEY = "chat.rightPanel.width";

const RIGHT_PANEL_FALLBACK = { min: 320, max: 500, fallback: 380 };

interface SenderDirectory {
  /** Display name by sender id. */
  [senderId: string]: string;
}

export interface ThreadPanelProps {
  /** Matrix room id of the thread's room. */
  roomId: string;
  /** Room name — shown in the subtitle ("in #<room>"). */
  roomName: string;
  /** The thread-root message. Pinned at the top of the scroll area. */
  root: TimelineEvent;
  /** Display name map for senders seen in the thread. Used for the
   *  root sender, subtitle summary, and reply senders. */
  senderDirectory?: SenderDirectory;
  /** Close handler — called by the header button and by Escape. */
  onClose: () => void;
  /** Optional click handler for a pre-formatted timestamp. Presentational
   *  timestamps are pre-computed upstream so the panel doesn't embed
   *  any i18n. */
  formatRelativeTime?: (timestamp: number) => string;
  archivedReadOnly?: boolean;
  removedReadOnly?: boolean;
  quoteComposerRef?: RefObject<QuoteComposerHandle | null>;
}

function resolveName(senderId: string, directory: SenderDirectory | undefined): string {
  return directory?.[senderId] ?? senderId;
}

/**
 * Build a directory by merging an explicit caller-supplied map with
 * the display names that travel inline on the events themselves.
 * The caller-supplied map wins (it usually carries the canonical
 * room-state display names — newer / more authoritative than the
 * sender stamp on a historical event), and the inline fallbacks
 * fill in the gaps for senders the caller didn't know about.
 *
 * Without this, a thread panel rendered without a `senderDirectory`
 * (the embed and standalone shells do not currently thread one
 * through) would surface raw matrix IDs (`@alice:matrix.org`) for
 * every sender. The events already carry the right name on their
 * `sender.displayName` field — we just have to consult it.
 */
function buildDirectory(
  root: TimelineEvent,
  replies: TimelineEvent[],
  caller: SenderDirectory | undefined,
): SenderDirectory {
  const out: SenderDirectory = {};
  for (const event of [root, ...replies]) {
    const id = event.sender?.id;
    const name = event.sender?.displayName;
    if (id && name && !(id in out)) out[id] = name;
  }
  // Caller wins: spread last so explicit entries overwrite the
  // event-derived fallbacks.
  if (caller) {
    for (const [id, name] of Object.entries(caller)) {
      out[id] = name;
    }
  }
  return out;
}

function RootBlock({
  client,
  roomId,
  event,
  senderName,
  relativeTime,
  ready,
  archivedReadOnly,
  onToggleReaction,
  media,
}: {
  client: Client | null;
  roomId: string;
  event: TimelineEvent;
  senderName: string;
  relativeTime: string;
  ready: boolean;
  archivedReadOnly: boolean;
  onToggleReaction: (emoji: string) => void | Promise<void>;
  /** Picture, player or download when the message is media. */
  media?: ReactNode;
}) {
  const [revealed, setRevealed] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const reactButtonRef = useRef<HTMLButtonElement | null>(null);
  const reactions = event.reactions;
  const gutterVisible = revealed || pickerOpen;

  const actions: MessageAction[] = [
    buildReactAction({
      pickerOpen,
      onToggle: () => setPickerOpen((v) => !v),
      buttonRef: reactButtonRef,
      disabled: !ready || archivedReadOnly,
    }),
  ];

  return (
    <div
      onMouseEnter={() => setRevealed(true)}
      onMouseLeave={() => setRevealed(false)}
      onFocus={() => setRevealed(true)}
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setRevealed(false);
      }}
      style={{
        position: "relative",
        padding: "var(--chat-space-3) var(--chat-space-4)",
        background: "var(--chat-surface-raised)",
        borderBottom: "1px solid var(--chat-surface-border)",
        display: "flex",
        gap: "var(--chat-space-3)",
      }}
    >
      <PersonAvatar
        client={client}
        name={senderName}
        avatarUrl={event.sender.avatarUrl}
        size={28}
      />
      <div data-feedback-redact="true" style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: "var(--chat-space-2)",
            marginBottom: 2,
          }}
        >
          <span
            style={{
              color: "var(--chat-fg-primary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-md-size)",
              lineHeight: "var(--chat-type-body-md-line)",
              fontWeight: 400,
            }}
          >
            {senderName}
          </span>
          <span
            style={{
              color: "var(--chat-fg-secondary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
            }}
          >
            {relativeTime}
          </span>
        </div>
        {media ?? (
          <div
            style={{
              margin: 0,
              color: "var(--chat-fg-primary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-md-size)",
              lineHeight: "var(--chat-type-body-md-line)",
              wordBreak: "break-word",
            }}
          >
            <MarkdownBody text={readBody(event)} />
          </div>
        )}
        {reactions && reactions.length > 0 && (
          <ReactionBar
            reactions={reactions}
            disabled={!ready || archivedReadOnly}
            client={client}
            roomId={roomId}
            onToggle={onToggleReaction}
          />
        )}
      </div>
      <div
        style={{
          flexShrink: 0,
          opacity: gutterVisible ? 1 : 0,
          visibility: gutterVisible ? "visible" : "hidden",
          transition: "opacity var(--chat-motion-fast) var(--chat-motion-ease)",
        }}
      >
        <MessageActions actions={actions} visible={gutterVisible} />
      </div>
      {pickerOpen && (
        <EmojiPicker
          anchor={reactButtonRef.current}
          onSelect={(emoji) => {
            setPickerOpen(false);
            void onToggleReaction(emoji);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

function ReplyRow({
  client,
  roomId,
  event,
  senderName,
  relativeTime,
  receipts,
  resolveReceiptName,
  resolveReceiptAvatar,
  editing,
  isOwn,
  ready,
  archivedReadOnly,
  onCloseEdit,
  onRecallOlder,
  onRecallNewer,
  onSaveEdit,
  onStartEdit,
  onDelete,
  onQuote,
  onToggleReaction,
  media,
}: {
  client: Client | null;
  roomId: string;
  event: TimelineEvent;
  senderName: string;
  relativeTime: string;
  receipts: Receipt[];
  /** Picture, player or download when the message is media. */
  media?: ReactNode;
  resolveReceiptName: (userId: string) => string;
  resolveReceiptAvatar: (userId: string) => string | null;
  editing: boolean;
  isOwn: boolean;
  ready: boolean;
  archivedReadOnly: boolean;
  onCloseEdit: (eventId: string) => void;
  onRecallOlder?: () => void;
  onRecallNewer?: () => void;
  onSaveEdit: (eventId: string, body: string) => Promise<void>;
  onStartEdit: (eventId: string) => void;
  onDelete: (eventId: string) => void | Promise<void>;
  onQuote: () => void;
  onToggleReaction: (emoji: string) => void | Promise<void>;
}) {
  // Compact density: avatar column 28px, 2px between rows — visual
  // match for the brief's "Compact" row type. Group collapsing is a
  // follow-up once the real MessageBubble lands and shares rules.
  const [revealed, setRevealed] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const reactButtonRef = useRef<HTMLButtonElement | null>(null);
  const reactions = event.reactions;
  const popoverOpen = pickerOpen || confirmingDelete;
  const gutterVisible = (revealed || popoverOpen) && !editing;

  // Threads don't nest, so no Reply action; no More menu in the panel yet.
  const actions: MessageAction[] = [
    buildReactAction({
      pickerOpen,
      onToggle: () => setPickerOpen((v) => !v),
      buttonRef: reactButtonRef,
      disabled: !ready || archivedReadOnly,
    }),
    buildQuoteAction({ onClick: onQuote }),
    ...(isOwn
      ? [
          buildEditAction({
            onClick: () => onStartEdit(event.id),
            disabled: !ready || archivedReadOnly,
          }),
          buildDeleteAction({
            onClick: () => setConfirmingDelete(true),
            disabled: !ready || archivedReadOnly,
          }),
        ]
      : []),
  ];

  return (
    <div
      onMouseEnter={() => setRevealed(true)}
      onMouseLeave={() => setRevealed(false)}
      onFocus={() => setRevealed(true)}
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setRevealed(false);
      }}
      style={{
        position: "relative",
        display: "grid",
        gridTemplateColumns:
          "var(--chat-timeline-avatar-column-compact) 1fr var(--chat-timeline-hover-action-gutter)",
        gap: "var(--chat-space-2)",
        padding: "var(--chat-timeline-within-group-gap) var(--chat-space-4) 0 var(--chat-space-4)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 2 }}>
        <PersonAvatar
          client={client}
          name={senderName}
          avatarUrl={event.sender.avatarUrl}
          size={24}
        />
      </div>
      <div data-feedback-redact="true" style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: "var(--chat-space-2)",
            marginBottom: 2,
          }}
        >
          <span
            style={{
              color: "var(--chat-fg-primary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-md-size)",
              lineHeight: "var(--chat-type-body-md-line)",
              fontWeight: 400,
            }}
          >
            {senderName}
          </span>
          <span
            style={{
              color: "var(--chat-fg-secondary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
            }}
          >
            {relativeTime}
          </span>
        </div>
        {editing ? (
          <InlineEdit
            initialBody={readBody(event)}
            onCancel={() => onCloseEdit(event.id)}
            onRecallOlder={onRecallOlder}
            onRecallNewer={onRecallNewer}
            onSave={async (next) => {
              await onSaveEdit(event.id, next);
              onCloseEdit(event.id);
            }}
          />
        ) : (
          (media ?? (
            <div
              style={{
                margin: 0,
                color: "var(--chat-fg-primary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-md-size)",
                lineHeight: "var(--chat-type-body-md-line)",
                wordBreak: "break-word",
              }}
            >
              <MarkdownBody text={readBody(event)} />
            </div>
          ))
        )}
        {!editing && reactions && reactions.length > 0 && (
          <ReactionBar
            reactions={reactions}
            disabled={!ready || archivedReadOnly}
            client={client}
            roomId={roomId}
            onToggle={onToggleReaction}
          />
        )}
        {!editing && !event.redacted && receipts.length > 0 && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
            <ReadReceipts
              receipts={receipts}
              resolveName={resolveReceiptName}
              resolveAvatar={resolveReceiptAvatar}
            />
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-start",
          opacity: gutterVisible ? 1 : 0,
          visibility: gutterVisible ? "visible" : "hidden",
          transition: "opacity var(--chat-motion-fast) var(--chat-motion-ease)",
        }}
      >
        <MessageActions actions={actions} visible={gutterVisible} />
      </div>
      {pickerOpen && (
        <EmojiPicker
          anchor={reactButtonRef.current}
          onSelect={(emoji) => {
            setPickerOpen(false);
            void onToggleReaction(emoji);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {confirmingDelete && (
        <DeleteConfirmDialog
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={async () => {
            await onDelete(event.id);
            setConfirmingDelete(false);
          }}
        />
      )}
    </div>
  );
}

function Shimmer() {
  return (
    <div
      aria-hidden
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--chat-space-2)",
        padding: "0 var(--chat-space-4)",
      }}
    >
      {[80, 60, 75, 50].map((widthPercent, i) => (
        <span
          key={i}
          style={{
            height: 12,
            width: `${widthPercent}%`,
            borderRadius: "var(--chat-radius-sm)",
            background: "var(--chat-surface-raised)",
          }}
        />
      ))}
    </div>
  );
}

function Empty() {
  return (
    <div
      style={{
        padding: "var(--chat-space-8) var(--chat-space-4)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        color: "var(--chat-fg-secondary)",
        textAlign: "center",
      }}
    >
      <Reply size={20} strokeWidth={ICON_STROKE} />
      <span
        style={{
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-md-size)",
          lineHeight: "var(--chat-type-body-md-line)",
          fontWeight: 500,
        }}
      >
        Start the thread
      </span>
      <span
        style={{
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          lineHeight: "var(--chat-type-body-sm-line)",
        }}
      >
        Only you and the people who reply will see this conversation.
      </span>
    </div>
  );
}

function Composer({
  onSend,
  placeholder,
  disabled,
  value: controlledValue,
  onValueChange,
  onAfterSend,
  onRecallOlder,
  attachButton,
  progressRows,
  onPasteFiles,
  client,
  resolveMentions,
  ownAgentGuard,
  ref,
}: {
  onSend: (body: string, options?: { formattedBody: string; mentions: string[] }) => Promise<void>;
  attachButton?: ReactNode;
  /** Upload rows, stacked above the input while files send. */
  progressRows?: ReactNode;
  onPasteFiles?: (files: readonly File[]) => void;
  placeholder: string;
  disabled: boolean;
  /** Optional controlled value (used by the draft binding). */
  value?: string | null;
  onValueChange?: (next: string) => void;
  /** Fired after a successful send so the draft binding can clear. */
  onAfterSend?: () => void;
  onRecallOlder?: () => void;
  client: Client | null;
  resolveMentions?: (
    trigger: string,
    query: string,
  ) => {
    immediate: Suggestion[];
    fetch: () => Promise<Suggestion[]>;
    reservedUnavailable?: boolean;
  };
  ownAgentGuard?: (scope: OwnAgentScope) => boolean;
  ref?: Ref<QuoteComposerHandle>;
}) {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState("");
  const value = isControlled ? (controlledValue ?? "") : internalValue;
  const isHydrating = isControlled && controlledValue === null;
  const [error, setError] = useState<string | null>(null);
  const setValue = useCallback(
    (next: string) => {
      if (isControlled) {
        onValueChange?.(next);
      } else {
        setInternalValue(next);
      }
      setError(null);
    },
    [isControlled, onValueChange],
  );
  const [sending, setSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const hasContent = value.trim().length > 0;

  const [mentions, setMentions] = useState<MentionToken[]>([]);
  const menuRef = useRef<AutocompleteMenuRef | null>(null);
  const fetchIdRef = useRef(0);
  const [suggestions, setSuggestions] = useState<readonly Suggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [reservedUnavailable, setReservedUnavailable] = useState(false);
  const [activeOptionId, setActiveOptionId] = useState<string | undefined>(undefined);

  const handleCommitMention = useCallback((token: MentionToken) => {
    setMentions((prev) => [...prev, token]);
  }, []);

  const pendingMentionEditRef = useRef<{ start: number; end: number; valueLength: number } | null>(
    null,
  );
  const handleBeforeInput = useCallback((event: FormEvent<HTMLTextAreaElement>) => {
    const el = event.currentTarget;
    pendingMentionEditRef.current = {
      start: el.selectionStart ?? 0,
      end: el.selectionEnd ?? el.selectionStart ?? 0,
      valueLength: el.value.length,
    };
  }, []);
  const handleInput = useCallback((event: FormEvent<HTMLTextAreaElement>) => {
    const el = event.currentTarget;
    if (el.dataset.composing === "true") {
      pendingMentionEditRef.current = null;
      return;
    }
    const range = pendingMentionEditRef.current;
    pendingMentionEditRef.current = null;
    if (!range) return;
    const inputType = inputEventType(event.nativeEvent);
    const selectedLength = Math.max(0, range.end - range.start);
    const insertedLength = Math.max(0, el.value.length - (range.valueLength - selectedLength));
    let edit: MentionEdit | null = null;
    if (inputType.startsWith("insert")) {
      edit = { start: range.start, removedLength: selectedLength, insertedLength };
    } else if (inputType.startsWith("delete")) {
      const removedLength =
        selectedLength > 0 ? selectedLength : Math.max(0, range.valueLength - el.value.length);
      const start =
        selectedLength > 0 || inputType.endsWith("Forward")
          ? range.start
          : range.start - removedLength;
      edit = { start: Math.max(0, start), removedLength, insertedLength: 0 };
    } else if (inputType.startsWith("history")) {
      setMentions((prev) => realignMentions(el.value, prev));
      return;
    }
    if (!edit) {
      setMentions([]);
      return;
    }
    setMentions((prev) => shiftMentions(prev, edit));
  }, []);

  const autocomplete = useAutocomplete({
    textareaRef,
    value,
    onChangeValue: setValue,
    onCommitMention: handleCommitMention,
  });

  useEffect(() => {
    const id = ++fetchIdRef.current;
    if (!resolveMentions || !autocomplete.open || !autocomplete.trigger) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      setReservedUnavailable(false);
      return;
    }
    const provider = resolveMentions(autocomplete.trigger, autocomplete.query);
    setSuggestions(provider.immediate);
    setReservedUnavailable(Boolean(provider.reservedUnavailable));
    if (provider.reservedUnavailable) {
      setSuggestionsLoading(false);
      return;
    }
    const needsFetch = provider.immediate.length < 10;
    if (!needsFetch) {
      setSuggestionsLoading(false);
      return;
    }
    setSuggestionsLoading(true);
    const handle = setTimeout(() => {
      void (async () => {
        try {
          const merged = await provider.fetch();
          if (id !== fetchIdRef.current) return;
          setSuggestions(merged);
        } catch {
          if (id !== fetchIdRef.current) return;
        } finally {
          if (id === fetchIdRef.current) setSuggestionsLoading(false);
        }
      })();
    }, AUTOCOMPLETE_FETCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [resolveMentions, autocomplete.open, autocomplete.trigger, autocomplete.query]);

  const handlePickSuggestion = useCallback(
    (suggestion: Suggestion, opts: { keepOpen: boolean }) => {
      if (suggestion.ownAgent && ownAgentGuard?.(suggestion.ownAgent) === false) {
        setError(OWN_AGENT_CHANGED_COPY);
        return;
      }
      autocomplete.select({
        text: suggestion.replacement,
        html: suggestion.replacementHtml,
        mxid: suggestion.mxid,
        ownAgent: suggestion.ownAgent,
        keepOpen: opts.keepOpen,
      });
    },
    [autocomplete, ownAgentGuard, setError],
  );

  const showMenu =
    Boolean(resolveMentions) &&
    autocomplete.open &&
    !isHydrating &&
    (suggestions.length > 0 || suggestionsLoading || reservedUnavailable);

  useImperativeHandle(
    ref,
    () => ({
      insertQuote(quote: string) {
        const prepend = value.length > 0;
        const next = prepend ? `${quote}\n\n${value}` : quote;
        if (prepend) {
          setMentions((prev) =>
            shiftMentions(prev, {
              start: 0,
              removedLength: 0,
              insertedLength: quote.length + 2,
            }),
          );
        }
        setValue(next);
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        requestAnimationFrame(() => {
          const end = next.length;
          el.setSelectionRange(end, end);
        });
      },
      focus() {
        textareaRef.current?.focus();
      },
    }),
    [setValue, value],
  );

  useAutoResizeTextarea(textareaRef, value, {
    min: { name: COMPOSER_TEXTAREA_MIN_HEIGHT_VAR, fallback: 40 },
    max: { name: COMPOSER_TEXTAREA_MAX_HEIGHT_VAR, fallback: 200 },
  });

  const submit = useCallback(async () => {
    if (!hasContent || sending || disabled) return;
    if (
      ownAgentGuard !== undefined &&
      mentions.some((t) => t.ownAgent !== undefined && !ownAgentGuard(t.ownAgent))
    ) {
      setError(OWN_AGENT_CHANGED_COPY);
      return;
    }
    const body = valueRef.current;
    const serialized = serialiseFormattedBody(body, mentions);
    const committedMentions = mentions;
    setSending(true);
    setError(null);
    setValue("");
    setMentions([]);
    try {
      if (serialized.formattedBody) {
        await onSend(body, {
          formattedBody: serialized.formattedBody,
          mentions: serialized.mentions,
        });
      } else {
        await onSend(body);
      }
      if (valueRef.current.length === 0) {
        onAfterSend?.();
      }
    } catch (err) {
      console.error("thread reply send failed", err);
      const current = valueRef.current;
      const restored = current.length === 0 ? body : `${body}\n\n${current}`;
      setValue(restored);
      setMentions(() => realignMentions(restored, committedMentions));
      setError(err instanceof Error ? err.message : "Failed to send reply");
    } finally {
      setSending(false);
    }
  }, [hasContent, sending, disabled, onSend, setValue, onAfterSend, ownAgentGuard, mentions]);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
      if (autocomplete.open && !e.nativeEvent.isComposing && menuRef.current?.handleKeyDown(e)) {
        e.preventDefault();
        return;
      }
      if (
        e.key === "ArrowUp" &&
        onRecallOlder &&
        !autocomplete.open &&
        !e.nativeEvent.isComposing &&
        !isHydrating &&
        value.trim() === ""
      ) {
        e.preventDefault();
        onRecallOlder();
        return;
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        void submit();
      }
    },
    [autocomplete.open, isHydrating, onRecallOlder, submit, value],
  );

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(e.clipboardData?.files ?? []);
    if (!onPasteFiles || files.length === 0) return;
    e.preventDefault();
    onPasteFiles(files);
  };

  return (
    <div style={{ padding: "var(--chat-space-3) var(--chat-space-4)" }}>
      {progressRows}
      <div
        className="chat-composer-wrapper chat-composer-wrapper--flush"
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: "var(--chat-space-2)",
          padding: "0 var(--chat-space-2) var(--chat-space-1)",
          opacity: disabled ? 0.55 : 1,
        }}
      >
        {attachButton}
        <textarea
          ref={textareaRef}
          onPaste={onPaste}
          onBeforeInput={handleBeforeInput}
          onInput={handleInput}
          aria-label={placeholder}
          role={showMenu ? "combobox" : undefined}
          aria-autocomplete={showMenu ? "list" : undefined}
          aria-expanded={showMenu || undefined}
          aria-controls={showMenu ? menuRef.current?.listboxId : undefined}
          aria-activedescendant={showMenu ? activeOptionId : undefined}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            if (isHydrating) return;
            setValue(e.target.value);
          }}
          onKeyDown={onKeyDown}
          disabled={disabled}
          readOnly={isHydrating}
          rows={1}
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            resize: "none",
            background: "transparent",
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
            minHeight: "var(--chat-composer-textarea-min-height)",
            maxHeight: "var(--chat-composer-textarea-max-height)",
            padding: "var(--chat-space-2) 0 var(--chat-space-1)",
          }}
        />
        <button
          type="button"
          aria-label="Send reply"
          disabled={!hasContent || sending || disabled}
          onClick={() => void submit()}
          className="chat-button"
          style={{
            width: "var(--chat-composer-send-button-size)",
            height: "var(--chat-composer-send-button-size)",
            flexShrink: 0,
            borderRadius: "var(--chat-radius-md)",
            border: "none",
            background: hasContent ? "var(--chat-cta-bg)" : "var(--chat-cta-disabled-bg)",
            color: hasContent ? "var(--chat-cta-fg)" : "var(--chat-cta-disabled-fg)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: hasContent && !sending && !disabled ? "pointer" : "default",
          }}
        >
          <Send size={14} strokeWidth={ICON_STROKE} />
        </button>
      </div>
      <AutocompleteMenu
        ref={menuRef}
        client={client}
        open={showMenu}
        suggestions={suggestions}
        loading={suggestionsLoading}
        emptyStateMessage={reservedUnavailable ? OWN_AGENT_UNAVAILABLE_COPY : "No matches"}
        textarea={textareaRef.current}
        caretIndex={autocomplete.triggerIndex}
        onPick={handlePickSuggestion}
        onClose={autocomplete.close}
        onActiveOptionIdChange={setActiveOptionId}
      />
      {error ? (
        <div
          role="alert"
          style={{
            marginTop: "var(--chat-space-2)",
            color: "var(--chat-state-danger)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
          }}
        >
          {error}
        </div>
      ) : null}
    </div>
  );
}

const ROOT_RECHECK_MIN_INTERVAL_MS = 15_000;

function ThreadReplyComposer({
  client,
  roomId,
  onSend,
  ref,
  placeholder,
  disabled,
  value,
  onValueChange,
  onAfterSend,
  onRecallOlder,
  attachButton,
  progressRows,
  onPasteFiles,
  resolveMentions,
  ownAgentGuard,
}: {
  client: Client;
  roomId: string;
  onSend: (body: string, options?: { formattedBody: string; mentions: string[] }) => Promise<void>;
  ref?: Ref<QuoteComposerHandle>;
  placeholder: string;
  disabled: boolean;
  value?: string | null;
  onValueChange?: (next: string) => void;
  onAfterSend?: () => void;
  onRecallOlder?: () => void;
  attachButton?: ReactNode;
  progressRows?: ReactNode;
  onPasteFiles?: (files: readonly File[]) => void;
  resolveMentions?: (
    trigger: string,
    query: string,
  ) => {
    immediate: Suggestion[];
    fetch: () => Promise<Suggestion[]>;
    reservedUnavailable?: boolean;
  };
  ownAgentGuard?: (scope: OwnAgentScope) => boolean;
}) {
  const gate = useOwnAgentMentionGate(client, roomId);
  const agentId = ownAgentUserId(getUserId(client));
  const mentionsFor = (body: string) => (agentId && body.includes(agentId) ? [agentId] : []);
  return (
    <>
      {gate.pending && (
        <AgentMentionPrompt
          busy={gate.pending.busy}
          error={gate.pending.error}
          onAdd={() => gate.choose("add")}
          onSend={() => gate.choose("send")}
          onCancel={gate.cancel}
        />
      )}
      <Composer
        ref={ref}
        onSend={(body, options) =>
          gate.wrap(
            (_b: string, _f: string | null, _m: string[], _s: boolean, _meta: ComposerSubmitMeta) =>
              onSend(_b, options),
          )(body, options?.formattedBody ?? null, options?.mentions ?? mentionsFor(body), false, {
            editedSinceSubmit: () => false,
          })
        }
        client={client}
        resolveMentions={resolveMentions}
        ownAgentGuard={ownAgentGuard}
        placeholder={placeholder}
        disabled={disabled}
        value={value}
        onValueChange={onValueChange}
        onAfterSend={onAfterSend}
        onRecallOlder={onRecallOlder}
        attachButton={attachButton}
        progressRows={progressRows}
        onPasteFiles={onPasteFiles}
      />
    </>
  );
}

/**
 * Right-side panel showing the root of a thread plus its chronological
 * replies. Mounts the composer in thread mode and forwards submits
 * through `sendThreadReply`. Callers are responsible for mounting this
 * conditionally — the panel assumes it is visible.
 */
export function ThreadPanel({
  roomId,
  roomName,
  root: openedRoot,
  senderDirectory,
  onClose,
  formatRelativeTime,
  archivedReadOnly: archived = false,
  removedReadOnly = false,
  quoteComposerRef,
}: ThreadPanelProps) {
  const { client, sendThreadReply, userId, searchUserDirectory } = useMatrix();
  const [rootRedacted, setRootRedacted] = useState(false);
  useEffect(() => {
    setRootRedacted(false);
    if (!client) return;
    let active = true;
    let inFlight = false;
    let trailing: ReturnType<typeof setTimeout> | null = null;
    let lastCheckAt = 0;
    const checkRoot = () => {
      const now = Date.now();
      if (inFlight) return Promise.resolve();
      const wait = ROOT_RECHECK_MIN_INTERVAL_MS - (now - lastCheckAt);
      if (wait > 0) {
        if (trailing === null) {
          trailing = setTimeout(() => {
            trailing = null;
            void checkRoot();
          }, wait);
        }
        return Promise.resolve();
      }
      inFlight = true;
      lastCheckAt = now;
      return getRoomEvent(client, roomId, openedRoot.id)
        .then((fetched) => {
          if (active && fetched && (fetched.redacted || fetched.content.type === "m.redacted")) {
            setRootRedacted(true);
          }
        })
        .catch(() => {})
        .finally(() => {
          inFlight = false;
        });
    };
    void checkRoot();
    const unsubscribe = subscribeTimeline(
      client,
      roomId,
      (events) => {
        const live = events.find((event) => event.id === openedRoot.id);
        if (!live) {
          void checkRoot();
          return;
        }
        if (live.redacted || live.content.type === "m.redacted") setRootRedacted(true);
      },
      { cache: true },
    );
    return () => {
      active = false;
      if (trailing !== null) clearTimeout(trailing);
      unsubscribe();
    };
  }, [client, roomId, openedRoot.id]);
  const root = useMemo<TimelineEvent>(
    () =>
      rootRedacted || openedRoot.redacted || openedRoot.content.type === "m.redacted"
        ? {
            ...openedRoot,
            redacted: true,
            reactions: undefined,
            content: { type: "m.redacted", originalType: "m.room.message" },
          }
        : openedRoot,
    [openedRoot, rootRedacted],
  );
  const {
    replies,
    latestReplyId,
    isLoading,
    isLoadingEarlier,
    earlierError,
    canLoadEarlier,
    loadEarlier,
    receiptsByEventId = new Map(),
  } = useThread(roomId, root.id, true, true);
  const draft = useDraft(roomId, root.id);

  const ownAgent = useOwnAgent(client, roomId, userId);
  const ownAgentRef = useRef<OwnAgentSnapshot | null>(null);
  ownAgentRef.current = buildOwnAgentSnapshot(ownAgent);

  const localMembers = useMemo<UserDirectoryEntry[]>(() => {
    const members = client ? getMembers(client, roomId) : [];
    return members
      .slice()
      .sort((a, b) => b.powerLevel - a.powerLevel)
      .map((member) => ({
        userId: member.userId,
        displayName: member.displayName || member.userId,
        avatarUrl: member.avatarUrl,
      }));
  }, [client, roomId]);

  const resolveMentions = useCallback(
    (
      trigger: string,
      query: string,
    ): {
      immediate: Suggestion[];
      fetch: () => Promise<Suggestion[]>;
      reservedUnavailable?: boolean;
    } => {
      if (trigger !== "@") {
        return { immediate: [], fetch: async () => [] };
      }
      return resolveUserSuggestions({
        query,
        local: localMembers,
        searchUserDirectory,
        allowRoomMention: client && userId ? canMentionRoom(client, roomId, userId) : false,
        ownAgent: ownAgentRef.current,
      });
    },
    [localMembers, searchUserDirectory, client, roomId, userId],
  );
  const { editMessage, deleteMessage, toggleReaction, ready } = useMessageActions(roomId);
  const {
    current: editingReplyId,
    origin: editOrigin,
    open: openEdit,
    recallOlder,
    recallNewer,
    exit: exitEdit,
  } = useEditRecall(replies, userId);

  const asideRef = useRef<HTMLElement | null>(null);
  const handleRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const openerKey = openedRoot.id;
  useEffect(() => {
    openerRef.current =
      typeof document !== "undefined" ? (document.activeElement as HTMLElement | null) : null;
  }, [openerKey]);
  const handleClose = useCallback(() => {
    const opener = openerRef.current;
    if (opener && document.contains(opener)) opener.focus();
    onClose();
  }, [onClose]);
  const [width, setWidth] = useState<number>(() => {
    const { min, max, fallback } = readWidthBounds("--chat-right-panel", RIGHT_PANEL_FALLBACK);
    const persisted = readPersistedWidth(WIDTH_STORAGE_KEY);
    // Clamp the persisted value against current token bounds so a
    // stale `localStorage` entry written before tokens were tightened
    // doesn't escape the intended range.
    if (persisted !== null) return Math.max(min, Math.min(max, persisted));
    return fallback;
  });
  const [isResizing, setIsResizing] = useState(false);
  const boundsRef = useRef(readWidthBounds("--chat-right-panel", RIGHT_PANEL_FALLBACK));

  // Re-read bounds lazily once the component is mounted — tokens are
  // available via getComputedStyle only after the stylesheet loads.
  // Also re-clamp current width against the freshly-read bounds.
  useEffect(() => {
    const bounds = readWidthBounds("--chat-right-panel", RIGHT_PANEL_FALLBACK);
    boundsRef.current = bounds;
    setWidth((w) => Math.max(bounds.min, Math.min(bounds.max, w)));
  }, []);

  // Escape closes the panel. Scoped to the panel: only closes when
  // focus is inside the aside or the event originated inside it, and
  // not already consumed by an inner dismissible (autocomplete, emoji
  // picker, native IME). Matches how modals + popovers compose.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (e.defaultPrevented) return;
      // `isComposing` covers IME compositions (CJK, etc.) where
      // Escape means "cancel composition", not "close panel".
      if (e.isComposing) return;
      const aside = asideRef.current;
      if (!aside) return;
      const target = e.target as Node | null;
      if (!target || !aside.contains(target)) return;
      e.stopPropagation();
      handleClose();
    };
    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
    };
  }, [handleClose]);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsResizing(true);
    // Capture on the handle itself — that's where release must go too
    // (capture is identity-specific to the element that called
    // `setPointerCapture`; releasing on the aside would throw).
    handleRef.current?.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (!isResizing) return;
      const aside = asideRef.current;
      if (!aside) return;
      const rect = aside.getBoundingClientRect();
      // The panel is anchored to the right edge — width grows when the
      // pointer moves leftward from the right boundary.
      const proposed = rect.right - e.clientX;
      const { min, max } = boundsRef.current;
      const clamped = Math.max(min, Math.min(max, proposed));
      setWidth(clamped);
    },
    [isResizing],
  );

  // Merge caller-supplied directory with display names that travel
  // inline on each event. Memoised on the inputs so the directory
  // identity is stable while replies don't change — the reply rows
  // beneath consume `directory` as a value, not a per-id resolver,
  // so identity stability buys us nothing here, but recomputing
  // unconditionally on each render would needlessly walk replies on
  // every keystroke in the composer.
  const directory = useMemo(
    () => buildDirectory(root, replies, senderDirectory),
    [root, replies, senderDirectory],
  );

  const receiptAvatars = useMemo(
    () => buildReceiptAvatarMap([root, ...replies], client ? getMembers(client, roomId) : []),
    [client, roomId, root, replies],
  );

  const rootSenderName = resolveName(root.sender.id, directory);
  const rootRelative = useMemo(
    () => (formatRelativeTime ? formatRelativeTime(root.ts) : ""),
    [formatRelativeTime, root.ts],
  );

  const quoteEvent = useCallback(
    (event: TimelineEvent) => {
      const payload: QuotePayload = {
        roomId,
        sender: resolveName(event.sender.id, directory),
        ts: event.ts,
        eventId: event.id,
        body: readBody(event),
      };
      insertQuote(quoteComposerRef, payload);
    },
    [roomId, directory, quoteComposerRef],
  );

  // `latestReplyId` lives in a ref so in-flight send callbacks always
  // reference the most recent reply, not whatever snapshot the
  // callback closed over. Without this, two quick sends both target
  // the older event as `m.in_reply_to`.
  const latestReplyIdRef = useRef(latestReplyId);
  useEffect(() => {
    latestReplyIdRef.current = latestReplyId;
  }, [latestReplyId]);

  const { push: pushToast } = useToast();
  const { openImage, lightbox } = useImageLightbox();
  const media = (event: TimelineEvent) =>
    client && isMediaEvent(event) ? (
      <MessageMediaBody client={client} event={event} onOpenImage={openImage} />
    ) : undefined;
  const upload = useFileUpload(() => ({
    rootId: root.id,
    latestReplyId: latestReplyIdRef.current || root.id,
  }));
  const archivedReadOnly = archived || removedReadOnly;
  const onFiles = useCallback(
    (files: readonly File[]) => {
      if (archivedReadOnly || root.redacted) return;
      void upload.uploadFiles(files, roomId);
    },
    [upload, roomId, archivedReadOnly, root.redacted],
  );
  const { uploads: pendingUploads, cancel: cancelUpload } = upload;
  useEffect(() => {
    if (!root.redacted && !archivedReadOnly) return;
    for (const record of pendingUploads) {
      if (record.status === "uploading") cancelUpload(record.id);
    }
  }, [root.redacted, archivedReadOnly, pendingUploads, cancelUpload]);
  const activeUpload = upload.uploads.find(
    (record) => record.status === "uploading" || record.status === "sending",
  );
  const progressRows = upload.uploads
    .filter(
      (record) =>
        record.status === "failed" || (record.status === "uploading" && record !== activeUpload),
    )
    .map((record) => (
      <UploadProgressRow
        key={record.id}
        record={record}
        onCancel={() => upload.cancel(record.id)}
        onRetry={() => {
          if (!root.redacted && !archivedReadOnly) upload.retry(record.id, roomId);
        }}
      />
    ));

  const onSend = useCallback(
    async (body: string, options?: { formattedBody: string; mentions: string[] }) => {
      if (archivedReadOnly) return;
      const fetched = client ? await getRoomEvent(client, roomId, root.id) : null;
      if (fetched && (fetched.redacted || fetched.content.type === "m.redacted")) {
        setRootRedacted(true);
        throw new Error("This message was removed");
      }
      if (options?.formattedBody) {
        await sendThreadReply(roomId, root.id, body, latestReplyIdRef.current, {
          formattedBody: options.formattedBody,
          mentions: options.mentions,
        });
        return;
      }
      await sendThreadReply(roomId, root.id, body, latestReplyIdRef.current);
    },
    [client, sendThreadReply, roomId, root.id, archivedReadOnly],
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  useThreadReadingPosition(scrollRef, `${roomId}|${root.id}`, replies);
  const readReceiptTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastReceiptEventIdRef = useRef<string | null>(null);

  const isAtThreadBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return false;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= AUTO_SCROLL_THRESHOLD_PX;
  }, []);

  const maybeScheduleThreadReceipt = useCallback(() => {
    if (!isAtThreadBottom()) return;
    const targetId = latestReplyIdRef.current;
    if (!targetId) return;
    if (readReceiptTimerRef.current) clearTimeout(readReceiptTimerRef.current);
    readReceiptTimerRef.current = setTimeout(() => {
      if (!client || !isAtThreadBottom()) return;
      if (lastReceiptEventIdRef.current === targetId) return;
      lastReceiptEventIdRef.current = targetId;
      void sendThreadReadReceipt(client, roomId, root.id, targetId);
    }, READ_RECEIPT_DEBOUNCE_MS);
  }, [client, roomId, root.id, isAtThreadBottom]);

  useEffect(() => {
    if (readReceiptTimerRef.current) clearTimeout(readReceiptTimerRef.current);
    lastReceiptEventIdRef.current = null;
  }, [roomId, root.id]);

  useEffect(
    () => () => {
      if (readReceiptTimerRef.current) clearTimeout(readReceiptTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (isLoading) return;
    maybeScheduleThreadReceipt();
  }, [maybeScheduleThreadReceipt, isLoading, replies]);

  const handleCloseEdit = useCallback(
    (eventId: string) => {
      if (editingReplyId !== eventId) return;
      exitEdit();
    },
    [editingReplyId, exitEdit],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (!isResizing) return;
      setIsResizing(false);
      // Release on the same element we captured on (the handle).
      // Pointer capture auto-releases on pointerup in every major
      // engine; calling release anyway closes the contract
      // symmetrically with `onPointerDown`.
      const handle = handleRef.current;
      if (handle && handle.hasPointerCapture?.(e.pointerId)) {
        handle.releasePointerCapture(e.pointerId);
      }
      persistWidth(WIDTH_STORAGE_KEY, width);
    },
    [isResizing, width],
  );

  const hasReplies = replies.length > 0;

  const visibleReplyCount = replies.filter((reply) => !reply.redacted).length;
  const canShowLoadEarlier = canLoadEarlier && visibleReplyCount > 0;

  const asideStyle: CSSProperties = {
    width,
    minWidth: "var(--chat-right-panel-min-width)",
    maxWidth: "var(--chat-right-panel-max-width)",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    // Honour the user's explicit width when rendered as a flex child
    // (the embed and standalone shells render the panel inside a
    // row-flex parent). Without this the panel compresses below its
    // resize-handle width as the container narrows; the previous
    // empty-slot placeholder also pinned `flexShrink: 0`.
    flexShrink: 0,
    background: "var(--chat-surface-app)",
    borderLeft: "1px solid var(--chat-surface-border)",
    transition: isResizing ? "none" : "width var(--chat-motion-base) var(--chat-motion-ease)",
    position: "relative",
    // Prevent accidental text selection while dragging the handle.
    userSelect: isResizing ? "none" : undefined,
  };

  const composerShared = {
    placeholder: root.redacted ? "This message was removed" : "Reply…",
    disabled: archivedReadOnly || root.redacted === true,
    value: draft.value,
    onValueChange: draft.setValue,
    onAfterSend: draft.markSent,
    onRecallOlder: recallOlder,
    attachButton:
      archivedReadOnly || root.redacted ? undefined : (
        <FileUploadButton
          onFiles={onFiles}
          onError={(message) => pushToast({ message, tone: "danger" })}
        />
      ),
    progressRows: progressRows.length > 0 ? progressRows : undefined,
    onPasteFiles: onFiles,
    resolveMentions,
    ownAgentGuard: ownAgent.guard,
  };

  return (
    <aside
      data-testid="thread-panel"
      aria-label="Thread"
      ref={asideRef}
      style={asideStyle}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {/* Resize handle — thin, hit area widened via padding. */}
      <div
        ref={handleRef}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize thread panel"
        onPointerDown={onPointerDown}
        style={{
          position: "absolute",
          left: -3,
          top: 0,
          bottom: 0,
          width: 6,
          cursor: "col-resize",
          // Transparent hit target; visual edge is the aside's own
          // left border.
          background: "transparent",
          zIndex: 1,
        }}
      />
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-3)",
          height: "var(--chat-timeline-room-header-height)",
          padding: "0 var(--chat-space-4)",
          borderBottom: "1px solid var(--chat-surface-border)",
          flexShrink: 0,
        }}
      >
        <Reply size={14} strokeWidth={ICON_STROKE} style={{ color: "var(--chat-fg-secondary)" }} />
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
          <span
            style={{
              color: "var(--chat-fg-primary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-md-size)",
              lineHeight: "var(--chat-type-body-md-line)",
              fontWeight: 500,
            }}
          >
            Thread
          </span>
          <span
            data-feedback-redact="true"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--chat-space-1)",
              color: "var(--chat-fg-secondary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <span>in #{roomName}</span>
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 4,
                height: 4,
                borderRadius: "var(--chat-radius-full)",
                background: "var(--chat-fg-muted)",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                minWidth: 0,
              }}
            >
              {readBody(root)}
            </span>
          </span>
        </div>
        <button
          type="button"
          aria-label="Close thread"
          onClick={handleClose}
          className="chat-button"
          style={{
            width: 28,
            height: 28,
            flexShrink: 0,
            borderRadius: "var(--chat-radius-md)",
            border: "none",
            background: "transparent",
            color: "var(--chat-fg-secondary)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            padding: 0,
          }}
        >
          <X size={14} strokeWidth={ICON_STROKE} />
        </button>
      </header>

      <DropZone onDrop={onFiles}>
        <div
          ref={scrollRef}
          onScroll={maybeScheduleThreadReceipt}
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            overflowAnchor: "none",
            // Reading to the bottom here advances the thread's own
            // threaded read marker, clearing the room's unread badge.
          }}
        >
          <RootBlock
            client={client}
            roomId={roomId}
            event={root}
            senderName={rootSenderName}
            relativeTime={rootRelative}
            ready={ready}
            archivedReadOnly={archivedReadOnly || root.redacted === true}
            onToggleReaction={(emoji) => toggleReaction(root.id, emoji, root.reactions ?? [])}
            media={media(root)}
          />
          <div style={{ padding: "var(--chat-space-3) 0" }}>
            {(canShowLoadEarlier || isLoadingEarlier || earlierError) && (
              <button
                type="button"
                data-cy="thread-load-earlier"
                onClick={() => {
                  if (!earlierError) void loadEarlier();
                }}
                disabled={isLoadingEarlier || !canLoadEarlier}
                className="chat-button flex min-h-11 w-full items-center text-left text-xs font-light text-muted-foreground outline-none transition-colors hover:text-foreground/90 focus-visible:shadow-neon-cyan disabled:opacity-60"
                style={{ paddingLeft: "var(--chat-space-3)", paddingRight: "var(--chat-space-3)" }}
              >
                {isLoadingEarlier
                  ? "Loading earlier replies…"
                  : earlierError
                    ? "Could not load earlier replies."
                    : "Load earlier replies"}
                {earlierError && (
                  <span data-cy="thread-earlier-error" className="ml-2 text-primary">
                    Retry
                  </span>
                )}
              </button>
            )}
            {isLoading && !hasReplies && <Shimmer />}
            {!isLoading && !hasReplies && <Empty />}
            {replies.map((reply) => (
              <div key={reply.localEchoId ?? reply.id} data-thread-reply={reply.id}>
                <ReplyRow
                  client={client}
                  roomId={roomId}
                  event={reply}
                  senderName={resolveName(reply.sender.id, directory)}
                  relativeTime={formatRelativeTime ? formatRelativeTime(reply.ts) : ""}
                  receipts={receiptsByEventId.get(reply.id) ?? []}
                  resolveReceiptName={(receiptUserId) => resolveName(receiptUserId, directory)}
                  resolveReceiptAvatar={(receiptUserId) =>
                    receiptAvatars.get(receiptUserId) ?? null
                  }
                  editing={reply.id === editingReplyId}
                  isOwn={reply.sender.id === userId}
                  ready={ready}
                  archivedReadOnly={archivedReadOnly}
                  onCloseEdit={handleCloseEdit}
                  onRecallOlder={editOrigin === "recall" ? recallOlder : undefined}
                  onRecallNewer={editOrigin === "recall" ? recallNewer : undefined}
                  onSaveEdit={editMessage}
                  onStartEdit={(eventId) => openEdit(eventId, "manual")}
                  onDelete={deleteMessage}
                  onQuote={() => quoteEvent(reply)}
                  onToggleReaction={(emoji) =>
                    toggleReaction(reply.id, emoji, reply.reactions ?? [])
                  }
                  media={media(reply)}
                />
              </div>
            ))}
          </div>
        </div>

        <footer
          style={{
            borderTop: "1px solid var(--chat-surface-border)",
            flexShrink: 0,
          }}
        >
          {client ? (
            <ThreadReplyComposer
              ref={quoteComposerRef}
              client={client}
              roomId={roomId}
              onSend={onSend}
              {...composerShared}
            />
          ) : (
            <Composer
              ref={quoteComposerRef}
              onSend={onSend}
              client={client ?? null}
              {...composerShared}
            />
          )}
        </footer>
      </DropZone>
      {lightbox}

      {/*
        Replies are reported as an accessible count too — useful when a
        screen reader catches the new-reply announcement and wants the
        running total.
      */}
      <span
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
        }}
      >
        {visibleReplyCount} {visibleReplyCount === 1 ? "reply" : "replies"}
      </span>
    </aside>
  );
}
